/**
 * Pure permission math shared by Simple-mode block chips and Advanced-mode
 * matrix. Mirrors the backend's `close_dependencies` so the FE stays honest
 * without a round-trip on every click. Also computes the
 * block-match status the Simple-mode "modified" chip depends on (R5/R6).
 */
import type {
  FeatureInfo,
  PermissionCatalog,
  PermissionInfo,
  RoleBlock,
  SubFeatureInfo,
} from "@/types/team"

// ---- Flat access helpers ---------------------------------------------------

/** Every PermissionInfo in the catalog, top-level + nested. */
export function flattenPermissions(
  catalog: PermissionCatalog,
): PermissionInfo[] {
  const flat: PermissionInfo[] = []
  for (const feature of catalog.features) {
    flat.push(...feature.permissions)
    for (const sub of feature.subFeatures) flat.push(...sub.permissions)
  }
  return flat
}

/** Every permission key on a single feature/sub-feature — the R7 counter source. */
export function keysOfGroup(group: FeatureInfo | SubFeatureInfo): string[] {
  const direct = group.permissions.map((p) => p.key)
  const nested =
    "subFeatures" in group
      ? group.subFeatures.flatMap((s) => s.permissions.map((p) => p.key))
      : []
  return [...direct, ...nested]
}

// ---- Dependency graph ------------------------------------------------------

interface DependencyGraph {
  requires: Map<string, string[]>
  dependents: Map<string, Set<string>>
}

function buildGraph(catalog: PermissionCatalog): DependencyGraph {
  const requires = new Map<string, string[]>()
  const dependents = new Map<string, Set<string>>()
  for (const perm of flattenPermissions(catalog)) {
    const reqs = perm.requires ?? []
    requires.set(perm.key, reqs)
    for (const req of reqs) {
      let set = dependents.get(req)
      if (!set) {
        set = new Set()
        dependents.set(req, set)
      }
      set.add(perm.key)
    }
  }
  return { requires, dependents }
}

/**
 * `keys` plus every permission each transitively requires. Order-preserving
 * where possible: the caller's ordering wins for the keys it named.
 */
export function closeDependencies(
  keys: string[],
  catalog: PermissionCatalog,
): string[] {
  const graph = buildGraph(catalog)
  const seen = new Set(keys)
  const stack = [...keys]
  while (stack.length) {
    const current = stack.pop()!
    for (const req of graph.requires.get(current) ?? []) {
      if (!seen.has(req)) {
        seen.add(req)
        stack.push(req)
      }
    }
  }
  const inSource = new Set(keys)
  return [...keys, ...[...seen].filter((k) => !inSource.has(k))]
}

/**
 * `remaining` with every dependent of any `removed` key stripped. Used when a
 * READ is toggled off: the WRITEs that named it as a prereq must go too,
 * otherwise the selection is unusable and the R7 counter would disagree with
 * itself. Cascades transitively.
 */
export function closeRemoval(
  remaining: string[],
  removed: string[],
  catalog: PermissionCatalog,
): string[] {
  const graph = buildGraph(catalog)
  const remainingSet = new Set(remaining)
  const toStrip = new Set<string>()
  const stack = [...removed]
  while (stack.length) {
    const current = stack.pop()!
    for (const dep of graph.dependents.get(current) ?? []) {
      if (remainingSet.has(dep) && !toStrip.has(dep)) {
        toStrip.add(dep)
        stack.push(dep)
      }
    }
  }
  return remaining.filter((k) => !toStrip.has(k))
}

// ---- Block matching --------------------------------------------------------

export type BlockStatus = "empty" | "matched" | "modified"

export interface BlockMatch {
  status: BlockStatus
  presentCount: number
  totalCount: number
}

/**
 * Which of the block's OWN permissions are present in the current selection.
 * "matched" = every own permission is on; "empty" = none are on;
 * "modified" = some but not all (R5).
 *
 * Deliberately matches against `block.permissions` and NOT the dep-closed
 * set: BASE contains every READ, so the closed set of A/B/C would
 * inherently overlap it and every non-BASE block would report "modified"
 * the moment BASE was on. Own-perms matching gives the natural semantics
 * an admin expects — "I clicked block A" ↔ "A is matched".
 */
export function matchBlock(
  block: RoleBlock,
  selected: Set<string>,
): BlockMatch {
  const own = block.permissions
  const present = own.filter((k) => selected.has(k)).length
  const status: BlockStatus =
    present === 0 ? "empty" : present === own.length ? "matched" : "modified"
  return { status, presentCount: present, totalCount: own.length }
}

/** All blocks' match state for the current selection — Simple mode reads this. */
export function matchBlocks(
  blocks: RoleBlock[],
  selected: string[],
): Map<string, BlockMatch> {
  const set = new Set(selected)
  const out = new Map<string, BlockMatch>()
  for (const block of blocks) out.set(block.key, matchBlock(block, set))
  return out
}

// ---- Simple ↔ Advanced switching (R6) --------------------------------------

/**
 * Grant this block: union its closed permissions into the current selection.
 * `matched` blocks reachable via inclusion (BASE contains every READ) still
 * light up in Simple mode without their permissions being counted twice —
 * the union handles that.
 */
export function grantBlock(
  block: RoleBlock,
  current: string[],
  catalog: PermissionCatalog,
): string[] {
  const closed = new Set(closeDependencies(block.permissions, catalog))
  const out = [...current]
  for (const k of closed) if (!out.includes(k)) out.push(k)
  return out
}

/**
 * Revoke this block: remove the block's own permissions AND cascade any dependent
 * WRITEs/ADMINs that had one of those reads as a prereq. Locked blocks (BASE)
 * are refused — the caller should not have offered the toggle.
 */
export function revokeBlock(
  block: RoleBlock,
  current: string[],
  catalog: PermissionCatalog,
): string[] {
  if (block.locked) return current
  const remaining = current.filter((k) => !block.permissions.includes(k))
  return closeRemoval(remaining, block.permissions, catalog)
}
